import './order-contract.scss';
import { validate as uuidValidate } from 'uuid';
import { AddProduct } from '../add-product/add-product';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Typography } from '../../../components/typography/typography';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { CalculateFinancialSummaryRequest } from '../../../clients/quipli-service/financials/financials';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Order, OrderStatus, OrderType } from '../../../clients/quipli-service/order/order';
import { OrderTotals } from '../create/totals';
import classNames from 'classnames';
import { ShippingType } from '../../../clients/quipli-service/delivery/delivery';
import { Location } from '../../../clients/quipli-service/location/location';
import { debounce } from 'lodash';
import { useAdmin } from '../../../hooks/use-admin-context';
import { toast } from '../../../util/toasts';
import { OrderSection } from '../order-section/order-section';
import { CustomSecuritytDeposit } from './custom-security-deposit';
import { BufferPeriodModal } from './buffer-period-modal';
import { UserAddress } from '../../../clients/quipli-service/user/user';
import { AddLineItemModal } from '../invoices/add-line-item-modal';
import { ConvertToCycleBillingModal } from './convert-cycle-billing';
import { OrderDeliverySelection } from '../create/order-delivery-selection';
import { TaxBreakdown } from '../../../clients/quipli-service/tax/tax';
import { RentalDatesModal } from './rental-dates-modal';
import { Toggle } from '../../../components/form/toggle';
import { ProductList } from './components/product-list/product-list';
import { useOrderManagement } from './hooks/use-order-management';
import { useFinancialSummary } from './hooks/use-financial-summary';
import { useOrderContractConfigurations } from './hooks/use-order-contract-configurations';
import { UseScanQRModal } from './hooks/use-scan-qr-modal';
import { ContractHeader } from './components/contract-header';
import { useHourReadingModal } from './hooks/use-hour-reading-modal';

export const OrderContract = ({
  order,
  location,
  canEdit,
  isEditMode,
  refresh,
  setEditMode,
  billingAddress,
  deliveryAddress,
  showTaxInclusive,
  setShowTaxInclusive,
}: PropsWithChildren<{
  order: Order;
  location: Location;
  canEdit: boolean;
  billingAddress?: UserAddress;
  deliveryAddress?: UserAddress;
  isEditMode: boolean;
  refresh: () => Promise<any>;
  setEditMode: (v: boolean) => void;
  showTaxInclusive: boolean;
  setShowTaxInclusive: React.Dispatch<React.SetStateAction<boolean>>;
}>) => {
  const { calculateFinancialSummary } = useAPI();

  const { hasPermissions, isSuperAdmin } = useAdmin();

  const isEditable = canEdit && isEditMode;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isAddLineItemModalOpen, setIsAddLineItemModalOpen] = useState<boolean>(false);
  const [isSecurityDepositModalOpen, setIsSecurityDepositModalOpen] = useState<boolean>(false);
  const [isBufferPeriodModalOpen, setIsBufferPeriodModalOpen] = useState<boolean>(false);
  const [isConvertToModalopen, setIsConvertToModalOpen] = useState<boolean>(false);
  const [isRentalDatesModalOpen, setIsRentalDatesModalOpen] = useState<boolean>(false);

  const [showSummary, setShowSummary] = useState<boolean>(true);
  const [deliveryDescriptions, setDeliveryDescriptions] = useState<{ productId: number; text: string }[]>([]);
  const [taxBreakdown, setTaxBreakdown] = useState<TaxBreakdown[] | undefined>(order.taxBreakdown);

  const {
    configurations,
    showRates,
    setShowRates,
    taxCodes,
    taxLabel,
    lockSecurityDeposits,
    isQrCodeEnabled,
    isCustomSecurityDepositEnabled,
    isCustomOrderRentalRatesEnabled,
    onlyShowStoreHoursOnDateTimePicker,
    showPricesWithTax,
  } = useOrderContractConfigurations();

  const {
    summary,
    setSummary,
    prevSummary,
    customDelivery,
    setCustomDelivery,
    customPickup,
    setCustomPickup,
    customDamageWaiver,
    setCustomDamageWaiver,
    customFees,
    setCustomFees,
    customAdditionalDiscount,
    setCustomAdditionalDiscount,
    customTax,
    setCustomTax,
    customTaxPercentage,
    setCustomTaxPercentage,
    coupon,
    setCoupon,
    selectedDelivery,
    setSelectedDelivery,
    customProductFees,
    setCustomProductFees,
    taxCode,
    setTaxCode,
    existingSecurityDeposit,
    calculateOnLoad,
    setInitStatesValues,
    getFinancialRequest,
  } = useFinancialSummary({
    order,
    location,
    configurations,
    showTaxInclusive,
  });

  const {
    orderInputs,
    productPrices,
    initProductPrices,
    updateProduct,
    deleteOrderProduct,
    addOrderProduct,
    cancelOrderManagementChanges,
    submitOrder,
  } = useOrderManagement({
    order,
    location,
    summary,
  });

  const { setHourReadingModalOpen, HourReadingModal } = useHourReadingModal({
    order,
    refresh,
  });

  const { QRScannerModal, setScanQrModalOpen } = UseScanQRModal({
    order,
    refresh,
    setHourReadingModalOpen,
  });

  const [editsValid, setEditsValid] = useState<boolean>(true);

  const submitOrderWrapper = async () => {
    if (!summary) {
      toast('Please add products.', 'error');
      return;
    }

    if (!selectedDelivery) {
      toast('Please select a delivery method.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      await submitOrder(billingAddress, deliveryAddress, selectedDelivery, taxBreakdown, taxCode, coupon);
      await refresh();

      setEditMode(false);
      toast('Order updated successfully.');
    } catch (err: any) {
      toast(err.msg || err.message || 'Error submitting order', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Call Only when editMode is true
  const calculateSummary = useCallback(
    debounce(async (request: CalculateFinancialSummaryRequest) => {
      setIsLoading(true);

      try {
        const response = await calculateFinancialSummary(request);

        console.log('Financial Summary Response:', response);
        if (order.status === OrderStatus.Approved && lockSecurityDeposits && existingSecurityDeposit) {
          // If security deposits are locked, replace the newly calculated security deposit with the locked one
          const nonSecurityDepositLineItems = response.lineItems.filter(
            (l) => ['ProductSecurityDeposit', 'SecurityDeposit'].indexOf(l.type) < 0,
          );
          response.lineItems = [...nonSecurityDepositLineItems, existingSecurityDeposit];
        }

        setSummary(response);
        if (response.error) {
          toast(response.error, 'error');
        }
      } catch (ex: any) {
        toast(ex.msg || 'Error calculating financial summary.', 'error');
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [order, lockSecurityDeposits, existingSecurityDeposit, deliveryAddress],
  );

  useEffect(() => {
    isEditMode && calculateSummary(getFinancialRequest(billingAddress, deliveryAddress, orderInputs));
  }, [
    isEditMode,
    customDelivery,
    customPickup,
    customTaxPercentage,
    customTax,
    coupon,
    customDamageWaiver,
    customFees,
    customProductFees,
    customAdditionalDiscount,
    billingAddress,
    deliveryAddress,
    selectedDelivery,
    orderInputs,
    location,
    taxCode,
    showTaxInclusive,
  ]);

  useEffect(() => {
    if (!isEditMode) {
      calculateOnLoad(initProductPrices, billingAddress, deliveryAddress, orderInputs);
    }
  }, [order]);

  const cancelChanges = async () => {
    cancelOrderManagementChanges();
    setSelectedDelivery(order.products[0].shippingType);

    setInitStatesValues();
    setSummary(prevSummary);
    setEditMode(false);
  };

  const handleRentalDateUpdate = async () => {
    await refresh();

    setIsRentalDatesModalOpen(false);
  };

  const newProducts = orderInputs.filter((input) => uuidValidate(input.id));

  return (
    <>
      <div data-cy="contract-summary" className={classNames('order-contract-summary', { hidden: !isEditable })}>
        <AddProduct
          orderId={order.id}
          userId={order.user.id}
          locationId={location.id}
          newOrderInput={addOrderProduct}
          isCycleBilled={order.isCycleBilled === true}
          cycleLength={order.cycleBillingTerms?.cycleLength}
          openOnStart={false}
          isTaxInclusive={showTaxInclusive}
          taxCodeId={taxCode?.id}
        />
      </div>

      {<QRScannerModal />}

      {<HourReadingModal />}

      {isAddLineItemModalOpen && order.invoices.length && (
        <AddLineItemModal
          isOpen={isAddLineItemModalOpen}
          close={() => {
            setIsAddLineItemModalOpen(false);
            refresh();
          }}
          invoice={order.invoices[0]}
          currency={location.currency ?? 'USD'}
        />
      )}

      {(isCustomSecurityDepositEnabled || order.rentalSecurity !== 0) && isSecurityDepositModalOpen && (
        <CustomSecuritytDeposit
          order={order}
          onSubmit={() => {
            refresh();
            setIsSecurityDepositModalOpen(false);
          }}
          onClose={() => setIsSecurityDepositModalOpen(false)}
        />
      )}

      {isBufferPeriodModalOpen && !!order && (
        <BufferPeriodModal
          order={order}
          onSubmit={() => {
            refresh();
            setIsBufferPeriodModalOpen(false);
          }}
          onClose={() => setIsBufferPeriodModalOpen(false)}
        />
      )}

      {!order.isCycleBilled && isConvertToModalopen && (
        <ConvertToCycleBillingModal
          order={order}
          onConfirm={async () => {
            await refresh();
            setIsConvertToModalOpen(false);
          }}
          onClose={() => setIsConvertToModalOpen(false)}
        />
      )}

      {isRentalDatesModalOpen && (
        <RentalDatesModal
          order={order}
          orderInputs={orderInputs}
          timezone={location.timezone}
          onUpdate={handleRentalDateUpdate}
          onClose={() => setIsRentalDatesModalOpen(false)}
        />
      )}

      <OrderSection className={classNames('min-w-full flex flex-col')}>
        <div className="min-w-full flex gap-[4px] flex-col sm:flex-row justify-between items-start sm:items-center">
          <div className="flex gap-3 items-center shrink-0">
            <Typography type="text-md" weight="medium" className="text-grey-800">
              Contract Details
            </Typography>
          </div>
          <div className="flex gap-3 items-center w-full sm:w-fit justify-start sm:justify-end">
            {canEdit && !isEditMode && (
              <Button
                className="button button-secondary"
                disabled={isLoading || showTaxInclusive}
                data-cy="update-order-button"
                onClick={() => setEditMode(true)}
              >
                <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)] hidden md:inline">
                  Update Order
                </Typography>
                <Icon icon="Edit" />
              </Button>
            )}
            <Button
              className={classNames('button !hidden md:!flex', {
                'button-secondary': !showRates,
                'button-grey': showRates,
              })}
              data-cy={`show_rates`}
              onClick={() => setShowRates(!showRates)}
            >
              <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)] ">
                {showRates ? 'Hide Rates' : 'Show Rates'}
              </Typography>
              <Icon icon="Money" />
            </Button>

            {isEditable ? (
              <>
                <Button
                  className="button button-secondary"
                  onClick={() => {
                    cancelChanges();
                  }}
                  disabled={isLoading}
                  data-cy="cancel-button"
                >
                  <Typography type="text-sm" weight="medium" className="text-[var(--grey-700)]">
                    Cancel
                  </Typography>
                </Button>
                <Button
                  className="button button-primary"
                  disabled={isLoading || !editsValid}
                  onClick={() => {
                    order && submitOrderWrapper();
                  }}
                  data-cy="save-changes-button"
                >
                  <Typography type="text-sm" weight="semi-bold" className="text-[white]">
                    Save Changes
                  </Typography>
                </Button>
              </>
            ) : (
              <ContractHeader
                order={order}
                showRates={showRates}
                setShowRates={setShowRates}
                setIsRentalDatesModalOpen={setIsRentalDatesModalOpen}
                isQrCodeEnabled={isQrCodeEnabled}
                setScanQrModalOpen={setScanQrModalOpen}
                isCustomSecurityDepositEnabled={isCustomSecurityDepositEnabled}
                setIsSecurityDepositModalOpen={setIsSecurityDepositModalOpen}
                hasPermissions={hasPermissions}
                isSuperAdmin={isSuperAdmin}
                setIsBufferPeriodModalOpen={setIsBufferPeriodModalOpen}
                setIsAddLineItemModalOpen={setIsAddLineItemModalOpen}
                setIsConvertToModalOpen={setIsConvertToModalOpen}
              />
            )}
          </div>
        </div>
        {showPricesWithTax && !isEditMode && order.user.chargeTax ? (
          <div
            data-cy="prices-with-tax"
            className="prices-with-tax flex gap-2 text-[0.9rem] justify-between items-center mt-2"
          >
            <div></div>
            <div className="flex gap-2 text-[0.9rem]">
              {showTaxInclusive ? (
                <Typography type={'text-sm'} data-cy="prices-with-tax-text">
                  (Unit Price and Total reflect {taxLabel} included, for preview only - not editable in this state)
                </Typography>
              ) : (
                <></>
              )}
              <Toggle
                onClick={async () => {
                  setShowTaxInclusive((prev) => !prev);
                  await refresh();
                }}
                checked={showTaxInclusive}
                data-cy="toggle-tax-inclusive"
              />
              View prices with {taxLabel} included
            </div>
          </div>
        ) : (
          <></>
        )}

        <div className="min-w-full mt-[5px] flex flex-col">
          <ProductList
            order={order}
            isEditMode={isEditMode}
            existingRentalProducts={order.products.filter((op) => op.type === OrderType.Rental)}
            existingSaleProducts={order.products.filter((op) => op.type === OrderType.Sale)}
            newRentalProducts={newProducts.filter((op) => op.type === OrderType.Rental)}
            newSaleProducts={newProducts.filter((op) => op.type === OrderType.Sale)}
            location={location}
            setEditsValid={setEditsValid}
            isEditable={isEditable}
            showRates={showRates}
            isCustomOrderRentalRatesEnabled={isCustomOrderRentalRatesEnabled}
            onlyShowStoreHoursOnDateTimePicker={onlyShowStoreHoursOnDateTimePicker}
            productPrices={productPrices}
            summary={summary}
            configurations={configurations}
            deliveryDescriptions={deliveryDescriptions}
            taxCode={taxCode}
            showTaxInclusive={showTaxInclusive}
            updateProduct={updateProduct}
            deleteOrderProduct={deleteOrderProduct}
            refresh={refresh}
            orderInputs={orderInputs}
            setCustomProductFees={setCustomProductFees}
            newProducts={newProducts}
          />

          <div className="min-w-full flex flex-col md:flex-row gap-2">
            <div className="flex-[0_1_65%] flex flex-col gap-3">
              {isEditMode && (
                <OrderDeliverySelection
                  locationId={order.locationId}
                  productTypes={orderInputs.map((op) => ({ id: op.productId, type: op.type }))}
                  selected={selectedDelivery}
                  setSelected={setSelectedDelivery}
                  setDeliveryDescriptions={setDeliveryDescriptions}
                  placeholder="Delivery"
                />
              )}

              {order.isCycleBilled && (
                <Typography
                  type="text-xs"
                  weight="regular"
                  data-cy="cycle-billing-note"
                  className="cycle-billing-note text-[black] w-[calc(100% - 300px)]"
                >
                  * All one time fees including any sale items will be part of 1st invoice. Any fee with unit ‘EA’
                  (each) will also be part of the recurring invoices.
                </Typography>
              )}
            </div>
            <div className="flex-[0_1_35%]">
              <div
                className="bg-[var(--grey-800)] p-[8px] h-[38px] w-[100%] rounded-[9px] flex justify-between items-center"
                onClick={() => setShowSummary(!showSummary)}
                data-cy="summary-toggle"
              >
                <Typography type="text-sm" weight="medium" className="summary-title text-[white]">
                  {order.isCycleBilled ? 'Taxes & Fees' : 'Order Summary'}
                </Typography>
                {showSummary ? <Icon icon="ChevronUp" stroke="white" /> : <Icon icon="ChevronDown" stroke="white" />}
              </div>
              <div
                data-cy="summary-container"
                className={classNames('w-[100%] rounded-[9px] order-contract-summary', {
                  hidden: !showSummary,
                })}
              >
                <OrderTotals
                  isCycleBilling={order.isCycleBilled}
                  isEditable={isEditable}
                  summary={summary}
                  tagPricing={order.user.tagPricing?.name}
                  coupon={coupon || '-NA-'}
                  setCoupon={setCoupon}
                  setCustomDelivery={setCustomDelivery}
                  setCustomPickup={setCustomPickup}
                  setCustomTax={setCustomTax}
                  setCustomTaxPercentage={setCustomTaxPercentage}
                  setCustomDamageWaiver={setCustomDamageWaiver}
                  setAdditionalDiscount={setCustomAdditionalDiscount}
                  setCustomFees={setCustomFees}
                  setTaxBreakdown={setTaxBreakdown}
                  taxCodeOptions={taxCodes ? { setTaxCode, taxCode, taxCodes } : undefined}
                  taxBreakdown={taxBreakdown}
                  isTaxAmountEditable={!order.isCycleBilled}
                  location={location}
                  isDelivery={selectedDelivery ? selectedDelivery !== ShippingType.SelfPickUp : false}
                  isTaxInclusive={showTaxInclusive}
                  taxLabel={taxLabel}
                />
              </div>
            </div>
          </div>
        </div>
      </OrderSection>
    </>
  );
};
